// PaySimple Subscriptions — now a thin config over the shared SubscriptionsWorkspace. The shell (header,
// stat tiles, filter, table, pager, mapping dialog) is shared with AuthNet; this file supplies only the
// PaySimple data: endpoints, stat tiles, columns, and its two dialogs (detail + backfill).
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SubscriptionsWorkspace from '../../components/recon/SubscriptionsWorkspace';
import CreatedOrderReceipts from '../../components/recon/CreatedOrderReceipts';
import DonorLink from '../../components/recon/DonorLink';
import { buildPaysimpleNav } from '../../config/recon/paysimpleNav';
import { fmtMoney, fmtDate } from '../../components/recon/reconShared';
import { get, post } from '../../utils/api';

const isActive = (s) => /active/i.test(String(s || ''));

// PaySimple's EXISTING mapping endpoints, handed to the shared SubscriptionMappingDialog — same UI as
// AuthNet, no data-source change.
const paysimpleMapApi = {
  suggest: async (sub) => {
    const q = String(sub?.donor_name || '').trim();
    if (q.length < 2) return { candidates: [] };
    const r = await get('/api/manage/paysimple/devotee-search', { q });
    return { candidates: (r?.devotees || []).slice(0, 5).map((d) => ({ ...d, reason: 'name match', confidence: 'medium' })) };
  },
  devoteeSearch: (q) => get('/api/manage/paysimple/devotee-search', { q }),
  productSearch: (q) => get('/api/manage/paysimple/product-search', { q }),
  customerPledges: (customerId) => get('/api/manage/paysimple/customer-pledges', { customer_id: customerId }),
  saveMapping: ({ sub, customerId, splits }) => post('/api/manage/paysimple-schedules/map', {
    schedule_id: sub.id, customer_id: customerId, ps_customer_id: sub.customer_id_ps, schedule_name: sub.name, splits,
  }),
};

function DetailField({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}
// Full subscription detail — opened by clicking the Sub ID.
function ScheduleDetailDialog({ sch, onClose }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dErr, setDErr] = useState('');
  useEffect(() => {
    if (!sch) { setRows(null); return undefined; }
    let cancel = false;
    (async () => {
      setLoading(true); setDErr('');
      try { const r = await get('/api/manage/paysimple-reconcile', { schedule: sch.id, year: 'all' }); if (!cancel) setRows(Array.isArray(r?.rows) ? r.rows : []); }
      catch (e) { if (!cancel) setDErr(e?.response?.data?.error || e?.message || 'Failed to load payments'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [sch]);
  if (!sch) return null;
  const m = sch.mapping;
  const statusColor = (st) => (st === 'matched' ? 'success' : st === 'needs_link' ? 'info' : st === 'unbooked' ? 'error' : 'warning');
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>Subscription {sch.id}<IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
          <DetailField label="Customer" value={sch.donor_name || '—'} />
          <DetailField label="Amount" value={`${fmtMoney(sch.amount)}/mo`} />
          <DetailField label="Status" value={sch.status || '—'} />
          <DetailField label="Next payment" value={sch.next_payment_date ? fmtDate(sch.next_payment_date) : '—'} />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Mapping</Typography>
        {m ? (
          <Box sx={{ mb: 2 }}>
            <Chip size="small" color="success" variant="outlined" label={m.customer_name || 'Mapped'} />
            <Box sx={{ mt: 0.5 }}>{(m.splits || []).map((sp, i) => <Typography key={i} variant="body2" sx={{ color: 'text.secondary' }}>{sp.label} · {fmtMoney(sp.amount)}</Typography>)}</Box>
          </Box>
        ) : <Typography variant="body2" sx={{ color: 'warning.main', mb: 2 }}>Not mapped to a devotee yet.</Typography>}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Payments{rows ? ` (${rows.length})` : ''}</Typography>
        {dErr && <Alert severity="error" sx={{ mb: 1 }}>{dErr}</Alert>}
        {loading ? <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          : (rows && rows.length) ? (
            <Table size="small">
              <TableHead><TableRow><TableCell>Tx ID</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Method</TableCell><TableCell>Status</TableCell><TableCell>Order</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.payment_id}>
                    <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'text.secondary' }}>{r.payment_id}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fmtMoney(r.amount_num)}</TableCell>
                    <TableCell>{r.method || '—'}</TableCell>
                    <TableCell><Chip size="small" variant="outlined" color={statusColor(r.status)} label={r.status} /></TableCell>
                    <TableCell>
                      {r.matched_order_number ? (
                        <>
                          <Typography variant="body2">#{r.matched_order_number}</Typography>
                          {r.order_items_label && <Typography variant="caption" sx={{ color: 'text.secondary' }}>{r.order_items_label}</Typography>}
                        </>
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>No stored payments for this subscription yet.</Typography>}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
// Bulk-book a mapped schedule's unbooked payments. Creates REAL orders. The preview re-checks each stored
// "unbooked" payment against the mapping, so months that already have an order are excluded; staff then tick
// exactly which of the remaining payments to book (all selected by default).
function BackfillDialog({ sch, onClose, onDone }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [bErr, setBErr] = useState('');
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => {
    if (!sch) { setPlan(null); setResult(null); setBErr(''); setSelected(new Set()); return undefined; }
    let cancel = false;
    (async () => {
      setLoading(true); setBErr('');
      try {
        const r = await get('/api/manage/paysimple-schedules/backfill-preview', { schedule_id: sch.id });
        if (!cancel) { setPlan(r); setSelected(new Set((r?.create || []).map((p) => String(p.payment_id)))); }
      }
      catch (e) { if (!cancel) setBErr(e?.response?.data?.error || e?.message || 'Preview failed'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [sch]);
  const run = async () => {
    setRunning(true); setBErr('');
    try { const r = await post('/api/manage/paysimple-schedules/backfill', { schedule_id: sch.id, payment_ids: Array.from(selected) }); setResult(r); if (onDone) onDone(r); }
    catch (e) { setBErr(e?.response?.data?.error || e?.message || 'Backfill failed'); }
    finally { setRunning(false); }
  };
  if (!sch) return null;
  const createRows = plan?.create || [];
  const alreadyBooked = plan?.already_booked || [];
  const dateByPayment = {};
  createRows.forEach((p) => { dateByPayment[String(p.payment_id)] = p.date; });
  const allChecked = createRows.length > 0 && selected.size === createRows.length;
  const someChecked = selected.size > 0 && selected.size < createRows.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(createRows.map((p) => String(p.payment_id))));
  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const selectedAmount = createRows.filter((p) => selected.has(String(p.payment_id))).reduce((a, p) => a + (Number(p.amount) || 0), 0);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>Create missing orders — subscription {sch.id}<IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>PaySimple · {sch.donor_name}</Typography>
        {bErr && <Alert severity="error" sx={{ mt: 1 }}>{bErr}</Alert>}
        {loading ? <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
          : result ? (
            <>
              <Alert severity={result.failed?.length ? 'warning' : 'success'} sx={{ mt: 1 }}>Created {result.created_count} order(s){result.failed?.length ? ` · ${result.failed.length} failed` : ''}.</Alert>
              <CreatedOrderReceipts orders={(result.created || []).map((c) => ({ order_number: c.order_number, amount: c.amount, date: dateByPayment[String(c.payment_id)] }))} />
            </>
          ) : plan ? (
            <Box sx={{ mt: 1 }}>
              {alreadyBooked.length > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{alreadyBooked.length} already booked · excluded</Typography>
              )}
              {(plan.prior_year_count || 0) > 0 && (
                <Alert severity="info" sx={{ mb: 1 }}>{plan.prior_year_count} payment(s) from prior years aren&apos;t bulk-created here — open the subscription and use <b>Create order</b> on each to pick the right year&apos;s product.</Alert>
              )}
              {createRows.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nothing to create{(plan.prior_year_count || 0) > 0 ? ' this year' : ''}.</Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>{createRows.length} to book · {fmtMoney(plan.total_amount)}</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox"><Checkbox size="small" checked={allChecked} indeterminate={someChecked} onChange={toggleAll} /></TableCell>
                        <TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Payment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {createRows.map((p) => {
                        const id = String(p.payment_id);
                        return (
                          <TableRow key={id} hover selected={selected.has(id)} onClick={() => toggleOne(id)} sx={{ cursor: 'pointer' }}>
                            <TableCell padding="checkbox"><Checkbox size="small" checked={selected.has(id)} onChange={() => toggleOne(id)} onClick={(e) => e.stopPropagation()} /></TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(p.date)}</TableCell>
                            <TableCell align="right">{fmtMoney(p.amount)}</TableCell>
                            <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{id}</Typography></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </Box>
          ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        {!result && createRows.length > 0 && (
          <Button variant="contained" color="success" onClick={run} disabled={running || loading || selected.size === 0}>
            {running ? 'Creating…' : `Create ${selected.size} order(s)${selected.size ? ` · ${fmtMoney(selectedAmount)}` : ''}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export const paysimpleSubsConfig = {
  provider: 'paysimple',
  title: 'Subscriptions',
  accounts: false,
  defaultStatus: 'all',
  blurb: 'Live recurring subscriptions from PaySimple.',
  syncTitle: 'Sync now — reload recurring subscriptions from PaySimple',
  buildNav: (active, account, navigate) => buildPaysimpleNav('subscriptions', navigate),
  load: async () => { const r = await get('/api/manage/paysimple-schedules'); return { subscriptions: Array.isArray(r?.schedules) ? r.schedules : [] }; },
  loadMaps: async () => (await get('/api/manage/paysimple-schedules/maps'))?.mappings || [],
  mapKeyOfMapping: (m) => String(m.schedule_id),
  mapKey: (s) => String(s.id),
  rowKey: (s) => String(s.id),
  unmap: (s) => post('/api/manage/paysimple-schedules/unmap', { schedule_id: s.id }),
  searchText: (s) => `${s.donor_name || ''} ${s.id} ${s.amount} ${s.mapping?.customer_name || ''}`,
  statusOptions: (ctx) => [{ value: 'all', label: 'All statuses' }, ...[...new Set(ctx.all.map((s) => s.status).filter(Boolean))].sort().map((v) => ({ value: v, label: v }))],
  matchesStatus: (s, statusFilter) => statusFilter === 'all' || String(s.status) === statusFilter,
  // The shell renders the shared tiles + columns; this just maps a PaySimple schedule to the common shape.
  normalize: (s) => ({
    name: null, // PaySimple schedules have no meaningful name ("RecurringPayment") → shared column shows —
    amount: Number(s.amount) || 0,
    status: s.status || null,
    statusColor: isActive(s.status) ? 'success' : /expir/i.test(String(s.status || '')) ? 'default' : 'warning',
    active: isActive(s.status),
    expired: /expir/i.test(String(s.status || '')),
    expiredNeedsReview: false,
    paidCount: s.stats?.paid_count ?? null,
    linkedCount: s.stats?.linked_count ?? 0,
    needsLinkCount: s.stats?.needs_link_count ?? 0,
    unbookedCount: s.stats?.unbooked_count ?? 0,
    failedCount: s.stats?.problem_count ?? 0,
    nextPaymentDate: s.next_payment_date || null,
    installments: s.stats?.paid_count != null ? String(s.stats.paid_count) : null,
  }),
  detailSlot: 'detail',
  mapApi: paysimpleMapApi,
  renderDialogs: (ctx) => (
    <>
      <ScheduleDetailDialog sch={ctx.slots.detail} onClose={() => ctx.closeSlot('detail')} />
      <BackfillDialog sch={ctx.slots.backfill} onClose={() => ctx.closeSlot('backfill')} onDone={(r) => { ctx.setToast(`Backfill: ${r?.created_count || 0} order(s) created`); ctx.reload(); }} />
    </>
  ),
};

export default function PaysimpleSubscriptionsNewPage() {
  return <SubscriptionsWorkspace config={paysimpleSubsConfig} />;
}
